import { $, el, aboveBotBadge } from './dom.js';
import { ct } from './i18n.js';

export interface RoleOption {
  id: string;
  name: string;
  hexColor: string | null;
  aboveBot?: boolean;
}

/**
 * Role picker in the form of chips.
 *
 * Three different implementations coexisted in the old code
 * (`m-managerrole.js`, `m-rolemanagement.js`, and an inline block in the
 * Tickets view). All built their HTML by string concatenation with `innerHTML`
 * and `onclick="removeRole('<%= role.name %>')"` attributes: a role named
 * `'); alert(1); //` ran arbitrary script.
 * Here everything goes through `textContent` and event listeners.
 */
export interface RolePickerOptions {
  container: HTMLElement;
  picker: HTMLSelectElement;
  roles: RoleOption[];
  initial: string[];
  max?: number;
  onChange?: (selected: string[]) => void;
}

export class RolePicker {
  private selected: string[];
  private readonly byId: Map<string, RoleOption>;

  constructor(private readonly options: RolePickerOptions) {
    this.selected = [...new Set(options.initial)];
    this.byId = new Map(options.roles.map((role) => [role.id, role]));

    options.picker.addEventListener('change', () => {
      const value = options.picker.value;
      options.picker.value = '';
      if (value) this.add(value);
    });

    this.render();
  }

  get value(): string[] {
    return [...this.selected];
  }

  set value(roleIds: string[]) {
    this.selected = [...new Set(roleIds)];
    this.render();
  }

  private add(roleId: string): void {
    if (this.selected.includes(roleId)) return;
    if (this.options.max && this.selected.length >= this.options.max) return;
    this.selected.push(roleId);
    this.commit();
  }

  private remove(roleId: string): void {
    if (!this.selected.includes(roleId)) return;
    this.selected = this.selected.filter((id) => id !== roleId);
    this.commit();
  }

  /**
   * Redraws, tells the caller, and tells the page.
   *
   * The last of the three was missing, and the asymmetry hid it: **adding** a
   * role goes through the native `<select>`, whose own bubbling `change` raised
   * the save bar. **Removing** one is a click on a button this class built, so
   * nothing bubbled and the bar stayed down — the change was real, held in
   * memory, and lost at the next page change.
   *
   * Dispatched on the chip container rather than on the `<select>`: the select
   * is reset to an empty value after each addition, and firing its `change`
   * from here would re-enter the listener that calls `add`.
   */
  private commit(): void {
    this.render();
    this.options.onChange?.(this.value);
    this.options.container.dispatchEvent(new Event('change', { bubbles: true }));
  }

  private render(): void {
    const { container } = this.options;
    container.replaceChildren();

    if (this.selected.length === 0) {
      container.append(
        el('span', {
          class: 'text-base-content/40 text-sm',
          text: ct('noRoleSelected', 'No role selected'),
        }),
      );
    }

    for (const roleId of this.selected) {
      const role = this.byId.get(roleId);

      const chip = el('span', {
        class: 'badge badge-lg gap-1.5 pr-1',
      });

      if (role?.hexColor) {
        chip.append(
          el('span', {
            class: 'size-2.5 rounded-full',
            style: { backgroundColor: role.hexColor },
          }),
        );
      }

      // A role deleted on Discord's side stays on display, but is flagged as such.
      chip.append(
        el('span', {
          text: role ? `@${role.name}` : ct('unknownRole', 'Unknown role ({id})', { id: roleId }),
          class: role ? '' : 'italic opacity-60',
        }),
      );

      if (role?.aboveBot) chip.append(aboveBotBadge());

      const removeButton = el('button', {
        class: 'btn btn-ghost btn-xs btn-circle',
        text: '✕',
        attrs: {
          type: 'button',
          'aria-label': ct('removeItem', 'Remove {name}', { name: role?.name ?? roleId }),
        },
      });
      removeButton.addEventListener('click', () => this.remove(roleId));
      chip.append(removeButton);

      container.append(chip);
    }

    this.syncPicker();
  }

  /** Hides the already-chosen roles from the picker. */
  private syncPicker(): void {
    for (const option of this.options.picker.options) {
      if (!option.value) continue;
      option.hidden = this.selected.includes(option.value);
    }
  }
}

/** A shortcut: builds a `RolePicker` from a root's `data-*` attributes. */
export function mountRolePicker(
  root: ParentNode,
  roles: RoleOption[],
  initial: string[],
  onChange?: (selected: string[]) => void,
): RolePicker | null {
  const container = $('[data-role-chips]', root);
  // Queried as a plain element: asking for `HTMLSelectElement` here is an
  // assertion, and the whole point below is that the assertion can be false.
  const picker = $('[data-role-picker]', root);
  if (!container || !picker) return null;

  /*
   * Refuses anything that is no longer a native `<select>`.
   *
   * An element carrying `data-select-menu` as well gets replaced by the select
   * component before this runs, and `RolePicker` then threw on `.options` -
   * taking the rest of the page's setup down with it. Failing here says which
   * element and why, instead of a `not iterable` deep in a stack trace.
   */
  if (!(picker instanceof HTMLSelectElement)) {
    throw new TypeError(
      `[data-role-picker] doit être un <select> natif : ${picker.tagName.toLowerCase()} reçu. ` +
        'Retirez `data-select-menu` de cet élément - les deux composants ne peuvent pas le partager.',
    );
  }

  return new RolePicker({ container, picker, roles, initial, onChange });
}
